"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import {
  ANOTHER_BRAND,
  ANOTHER_BRAND_INSTRUCTION,
  MAKER_TERMS_LINKS,
  makerTermsLinkFor,
} from "@/lib/warranty/maker-links"

// FRIDGE-1250 — owner rule 09/18/2026: never restate another company's
// warranty as a Fridge.com claim. FRIDGE-1122, owner answer 09/24/2026:
// "Remove those promises. Explain coverage types and link to each
// manufacturer's exact terms." The per-brand years table is gone. The tool
// sends the shopper to the maker's own warranty or support page (the verified
// list in lib/warranty/maker-links.ts, shared with /warranty) and explains what
// to look for in the statement. Nothing here is attributed to any one brand.

/** What to look for in any maker's warranty statement. */
const STATEMENT_CHECKLIST = [
  {
    t: "Standard parts & labor",
    d: "How long the whole unit is covered, with both parts and the technician's labor paid.",
  },
  {
    t: "Compressor and sealed system",
    d: "Whether the compressor and the sealed refrigeration system are covered longer than the standard term, and for how long.",
  },
  {
    t: "Parts-only coverage",
    d: "Whether that longer coverage pays for labor too, or only for the part. Labor coverage is usually only the first year.",
  },
  {
    t: "Exclusions",
    d: "What the statement lists as not covered, and anything that voids coverage.",
  },
  {
    t: "Registration",
    d: "Whether you must register the unit, and by when, for coverage to apply.",
  },
]

/** Exclusions many statements share. Never attributed to a brand; the statement is the final word. */
const COMMON_EXCLUSIONS = [
  "Cosmetic damage (dents, scratches)",
  "Damage from power surges",
  "Consumable parts such as water filters and light bulbs",
  "Installation or delivery damage",
  "Commercial use of a residential model",
]

const FALLBACK_RELATED = [
  "warranty-expiration-calculator",
  "refrigerator-warranty-comparison",
  "extended-warranty-break-even-calculator",
  "refrigerator-repair-vs-replace-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function WarrantyCoverageLookupClient({ toolData }: { toolData?: ToolComponentData }) {
  const meta = toolData ?? TOOL_METADATA["warranty-coverage-lookup"]

  // Hooks first, THEN the early return (FRIDGE-307). The `meta` guard below
  // used to sit above these two hooks: React requires every hook to be called
  // in the same order on every render, and a component that returns early on
  // one render and reaches the hooks on the next crashes with "Rendered more
  // hooks than during the previous render". Neither hook reads `meta`, so
  // moving them up changes nothing about what renders.
  const [brand, setBrand] = useState<string>(MAKER_TERMS_LINKS[0].brand)

  const maker = useMemo(() => makerTermsLinkFor(brand), [brand])

  if (meta === undefined) return null

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What does your refrigerator's warranty"
      heroTitleItalic="actually"
      heroTitlePost="cover?"
      heroSubtitle="Pick your brand and go straight to the maker's own warranty page. Then use the checklist to read your statement: how long each layer of coverage lasts, what is parts-only, what is excluded, and whether you must register."
      heroMeta={["⏱ ~30 sec", "Links to each maker's own terms"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: String(MAKER_TERMS_LINKS.length), label: "Maker warranty pages linked", note: "Each maker's own site" },
            { value: String(STATEMENT_CHECKLIST.length), label: "Things to check in your statement", note: "Same list for every brand" },
            { value: String(COMMON_EXCLUSIONS.length), label: "Common exclusions to look for", note: "Confirm in your statement" },
            { value: "Model #", label: "What the maker's page asks for", note: "On the label inside the fridge" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Why doesn't this tool list each brand's warranty length?",
          answer:
            "Warranty terms are set by each maker, differ by model and model year, and can change. The statement for your exact model is the only reliable source, so this tool sends you to the maker's own warranty or support page instead of restating its terms. Enter your model number there to see the statement for your unit.",
        },
        {
          question: "Do I have to register the unit for the warranty to be valid?",
          answer:
            "It depends on the maker and the model. Your warranty statement says whether registration is required and by when. Even when it is optional, registering puts your model and serial number on file, which can speed up a future service claim.",
        },
        {
          question: "What's typically excluded from the standard warranty?",
          answer:
            "Many statements exclude cosmetic damage (dents, scratches), damage from power surges, consumable parts such as water filters and light bulbs, installation or delivery damage, and commercial use of a residential model. Your own statement is the final word on what yours excludes.",
        },
        {
          question: "What's the difference between compressor and sealed-system coverage?",
          answer:
            "The compressor is one component of the sealed refrigeration system. Sealed-system coverage typically also includes the evaporator, condenser, and the brazed connecting tubing. Where either is covered beyond the standard term, that longer coverage is often parts-only — check whether yours pays for the technician's labor.",
        },
        {
          question: "Should I buy the extended warranty?",
          answer:
            "It depends on how long you'll keep the unit and what your manufacturer's warranty already covers. Check your statement first, then run the extended-warranty break-even calculator with a realistic repair cost — for many residential refrigerators, the math is closer than the upsell suggests.",
        },
      ]}
      relatedTools={buildRelated((meta as ToolComponentData)?.relatedTools ?? [], "warranty-coverage-lookup")}
      cta={{
        title: "Shopping for a new refrigerator?",
        body: "Browse side-by-side refrigerators against live retailer pricing, then check the maker's warranty for the model you pick.",
        buttonLabel: "Browse side-by-side refrigerators",
        buttonHref: "/side-by-side-refrigerators",
      }}
    >
      {/* ============ LOOKUP ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Pick the brand.
                <br />
                Get the maker&apos;s terms.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Each link goes to the maker&apos;s own warranty or support page. Fridge.com does not
                restate any maker&apos;s terms.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label
                    htmlFor="warranty-lookup-brand"
                    className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2"
                  >
                    Refrigerator brand
                  </label>
                  <select
                    id="warranty-lookup-brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    {MAKER_TERMS_LINKS.map((m) => (
                      <option key={m.brand} value={m.brand}>
                        {m.brand}
                      </option>
                    ))}
                    <option value={ANOTHER_BRAND}>{ANOTHER_BRAND}</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Terms differ by model — have your model number ready
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you change brand
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Your maker&apos;s own terms
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>

                  {maker ? (
                    <a
                      href={maker.href}
                      data-maker-terms={maker.brand}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 rounded-lg px-5 py-4 bg-white text-gray-900 font-extrabold text-[16px] sm:text-[18px] tracking-tight hover:bg-white/90 mb-4"
                    >
                      <span className="min-w-0 break-words">{maker.label}</span>
                      <ArrowUpRight className="w-5 h-5 flex-shrink-0 text-[#0469f0]" aria-hidden />
                    </a>
                  ) : (
                    <div className="rounded-lg px-5 py-4 bg-white/10 border border-white/15 text-[15px] font-semibold mb-4">
                      {ANOTHER_BRAND_INSTRUCTION}
                    </div>
                  )}

                  <p className="text-[14px] text-white/85 leading-[1.55] mb-3">
                    {maker
                      ? "Opens the maker's site in a new tab. Enter your model number there to find the warranty statement for your exact unit."
                      : "The warranty statement for your exact unit is listed there by model number."}
                  </p>
                  <p className="text-[13px] text-white/75 leading-[1.55]">
                    The statement also ships in the box, usually inside the use-and-care guide. It is the
                    only authoritative source for your coverage.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <details className="mb-6 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
            <summary className="cursor-pointer font-semibold">Browse all manufacturer links</summary>
            <ul className="my-3 list-disc space-y-2 pl-5">
              {MAKER_TERMS_LINKS.map(link => <li key={link.brand}>
                <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-[#0469f0] underline">{link.label}</a>
              </li>)}
            </ul>
            <p>{ANOTHER_BRAND}: {ANOTHER_BRAND_INSTRUCTION}</p>
          </details>

          {/* What to check / commonly excluded */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#0469f0] mb-3">
                What to check in your warranty statement
              </h3>
              <ol className="space-y-2.5 text-[14px] text-gray-700 list-none p-0 m-0">
                {STATEMENT_CHECKLIST.map((item, i) => (
                  <li key={item.t} className="grid grid-cols-[auto_1fr] gap-2.5">
                    <span className="font-mono text-[12px] font-bold text-[#ff9951] pt-[2px]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>
                      <strong className="text-gray-900">{item.t}.</strong> {item.d}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#ff9951] mb-3">
                Commonly excluded — confirm in yours
              </h3>
              <ul className="space-y-2 text-[14px] text-gray-700">
                {COMMON_EXCLUSIONS.map((x) => (
                  <li key={x}>• {x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Compare brands"
          title="Side-by-side refrigerators across major brands"
          description="Compare capacity, features, and live prices in one place."
          collectionType="side-by-side"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Your maker&apos;s <em className="italic text-[#ff9951]">terms.</em> Nothing restated.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Warranty terms are set by each maker and change by model and model year, so this tool
                never restates them. It links each maker&apos;s own warranty or support page and tells
                you what to look for once you are there.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Find your model number",
                  d: "It is on the rating label, usually inside the fresh-food compartment. The maker's page uses it to find the statement for your exact unit.",
                },
                {
                  n: "02",
                  t: "Open your maker's page",
                  d: "Use the link above. Where a maker publishes no standalone warranty page, the link goes to its owner support center, which lists the statement by model.",
                },
                {
                  n: "03",
                  t: "Check the five items",
                  d: "Parts and labor, compressor and sealed system, parts-only terms, exclusions, and registration. Then enter the lengths in the warranty expiration calculator to see your dates.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door refrigerators"
          description="Premium designs across every brand — compare specs and prices side by side."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
